import React from 'react';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';

import { serviceStatuses, serviceStopStatuses, extendedServiceStopStatuses } from 'types/timetableServices';
import { localeToLanguageKey, getTimetableStatus, getTimetableStatusSummary } from 'utils/timetableServices';

import { TimetableItem, TimetableItemDetails, TimetableStationName } from './StopsSummary.style';

import StopArrival from './StopArrival';
import StopCancelled from './StopCancelled';
import StopDeparture from './StopDeparture';

const StopSummary = ({ serviceStatus, setIsLimitedService, originUic, destinationUic, serviceStop, intl: { locale }, dark }) => {
  const { commercialArrivalDateTime, commercialDepartureDateTime, scheduledDepartureDateTime, scheduledArrivalDateTime, stopNumber } =
    getTimetableStatus({ serviceStop });

  const { extendedArrivalStatus, arrivalDateTime, extendedDepartureStatus, departureDateTime } = getTimetableStatusSummary({ serviceStop });

  const {
    station: { uic, name }
  } = serviceStop;
  const languageKey = localeToLanguageKey({ locale });
  const stationName = name[languageKey];

  const firstStop = stopNumber === 0;
  const lastStop = !commercialDepartureDateTime;
  const originStop = uic === originUic;
  const destinationStop = uic === destinationUic;

  let isCancelled = false;
  let isLimited = false;

  if (serviceStatus === serviceStopStatuses.CANCELLED) {
    isCancelled = true;
    isLimited = true;
  } else if (firstStop && extendedDepartureStatus === extendedServiceStopStatuses.CANCELLED) {
    isCancelled = true;
  } else if (lastStop && extendedArrivalStatus === extendedServiceStopStatuses.CANCELLED) {
    isCancelled = true;
  } else if (
    extendedArrivalStatus === extendedServiceStopStatuses.CANCELLED &&
    extendedDepartureStatus === extendedServiceStopStatuses.CANCELLED
  ) {
    isCancelled = true;
  }

  if (originStop && extendedDepartureStatus === extendedServiceStopStatuses.CANCELLED) {
    isLimited = true;
  } else if (destinationStop && extendedArrivalStatus === extendedServiceStopStatuses.CANCELLED) {
    isLimited = true;
  }

  if (isLimited && setIsLimitedService) {
    setIsLimitedService(isLimited);
  }

  if (isCancelled) {
    return (
      <StopCancelled
        arrivalDateTime={arrivalDateTime}
        departureDateTime={departureDateTime}
        stationName={stationName}
        stopNumber={stopNumber}
        dark={dark}
      />
    );
  }

  let stationNameStatus = extendedDepartureStatus;

  if (lastStop) {
    stationNameStatus = extendedArrivalStatus;
  } else if (
    extendedDepartureStatus === extendedServiceStopStatuses.CANCELLED &&
    extendedArrivalStatus !== extendedServiceStopStatuses.CANCELLED
  ) {
    stationNameStatus = extendedArrivalStatus;
  }

  return (
    <TimetableItem data-testid={`timetable-service-stop-${stopNumber}`} dark={dark}>
      <StopArrival
        extendedArrivalStatus={extendedArrivalStatus}
        extendedDepartureStatus={extendedDepartureStatus}
        arrivalDateTime={arrivalDateTime}
        commercialArrivalDateTime={commercialArrivalDateTime}
        departureDateTime={departureDateTime}
        commercialDepartureDateTime={commercialDepartureDateTime}
        scheduledDepartureDateTime={scheduledDepartureDateTime}
        scheduledArrivalDateTime={scheduledArrivalDateTime}
      />
      <TimetableItemDetails>
        <TimetableStationName status={stationNameStatus}>{stationName}</TimetableStationName>
        {!firstStop && !lastStop && (
          <StopDeparture
            extendedDepartureStatus={extendedDepartureStatus}
            departureDateTime={departureDateTime}
            commercialDepartureDateTime={commercialDepartureDateTime}
            scheduledDepartureDateTime={scheduledDepartureDateTime}
          />
        )}
      </TimetableItemDetails>
    </TimetableItem>
  );
};

StopSummary.propTypes = {
  dark: PropTypes.bool,
  serviceStatus: PropTypes.oneOf(Object.keys(serviceStatuses)),
  serviceStop: PropTypes.shape({
    model: PropTypes.shape({
      arrivalStatus: PropTypes.oneOf(Object.keys(serviceStopStatuses)),
      commercialArrivalDateTime: PropTypes.string,
      commercialDepartureDateTime: PropTypes.string,
      departureStatus: PropTypes.oneOf(Object.keys(serviceStopStatuses)),
      expectedArrivalDateTime: PropTypes.string,
      expectedDepartureDateTime: PropTypes.string,
      stopNumber: PropTypes.number
    }),
    station: PropTypes.shape({
      name: PropTypes.shape({
        en: PropTypes.string,
        de: PropTypes.string,
        fr: PropTypes.string,
        nl: PropTypes.string
      }),
      uic: PropTypes.string,
      shortCode: PropTypes.string
    })
  }),
  setIsLimitedService: PropTypes.func,
  originUic: PropTypes.string,
  destinationUic: PropTypes.string,
  intl: PropTypes.shape({
    locale: PropTypes.string.isRequired
  })
};

export default injectIntl(StopSummary);
